import * as React from "react";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Command as CommandIcon,
  LayoutGrid,
  Minus,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui/primitives/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@repo/ui/primitives/command";
import { DialogDescription, DialogTitle } from "@repo/ui/primitives/dialog";
import { Input } from "@repo/ui/primitives/input";
import {
  groupSidebarPagesForWorkspace,
  sidebarPageMatchesQuery,
  toggleSidebarPageSelection,
} from "@/config/sidebar-nav.utils.js";
import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import { ensureSidebarLabelNamespaces } from "@/lib/i18n-sidebar-label-namespaces.js";
import {
  resolveSidebarWorkspacePreferences,
  useSidebarPreferencesStore,
} from "@/store/sidebar-preferences.store.js";

export function GlobalCommandCenter({
  pages = [],
  modules = [],
  customWorkspaces: customWorkspacesProp,
  activeWorkspace = "all",
  preferenceScopeKey,
  onSelectWorkspace = () => true,
  open,
  onOpenChange,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("header");
  const storedWorkspacePreferences = useSidebarPreferencesStore((state) => (
    preferenceScopeKey ? state.workspacePreferencesByScope?.[preferenceScopeKey] : undefined
  ));
  const { favoritePageUrls, customWorkspaces: storedCustomWorkspaces } = React.useMemo(
    () => resolveSidebarWorkspacePreferences(storedWorkspacePreferences),
    [storedWorkspacePreferences],
  );
  const customWorkspaces = customWorkspacesProp ?? storedCustomWorkspaces;
  const toggleFavoritePage = useSidebarPreferencesStore((state) => state.toggleFavoritePage);
  const createCustomWorkspace = useSidebarPreferencesStore((state) => state.createCustomWorkspace);
  const updateCustomWorkspace = useSidebarPreferencesStore((state) => state.updateCustomWorkspace);
  const deleteCustomWorkspace = useSidebarPreferencesStore((state) => state.deleteCustomWorkspace);
  const [view, setView] = React.useState("main");
  const [commandQuery, setCommandQuery] = React.useState("");
  const [workspaceDraft, setWorkspaceDraft] = React.useState(null);
  const [workspacePageQuery, setWorkspacePageQuery] = React.useState("");

  const showView = React.useCallback((nextView) => {
    setCommandQuery("");
    setView(nextView);
  }, []);

  const handleOpenChange = React.useCallback((nextOpen) => {
    if (!nextOpen) {
      showView("main");
      setWorkspaceDraft(null);
      setWorkspacePageQuery("");
    } else {
      void ensureSidebarLabelNamespaces();
    }
    onOpenChange(nextOpen);
  }, [onOpenChange, showView]);

  React.useEffect(() => {
    showView("main");
    setWorkspaceDraft(null);
    setWorkspacePageQuery("");
  }, [preferenceScopeKey, showView]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        handleOpenChange(!open);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleOpenChange, open]);

  const favoritePages = React.useMemo(() => {
    const pageByUrl = new Map(pages.map((page) => [page.url, page]));
    return favoritePageUrls.map((url) => pageByUrl.get(url)).filter(Boolean);
  }, [favoritePageUrls, pages]);
  const favoriteUrls = React.useMemo(() => new Set(favoritePageUrls), [favoritePageUrls]);
  const otherPages = React.useMemo(
    () => pages.filter((page) => !favoriteUrls.has(page.url)),
    [favoriteUrls, pages],
  );

  const activeWorkspaceLabel = React.useMemo(() => {
    if (activeWorkspace === "all") {
      return t("commandCenter.modules.items.all", { defaultValue: "All modules" });
    }
    const module = modules.find(({ key }) => key === activeWorkspace);
    if (module) {
      return t(`commandCenter.modules.items.${module.key}`, { defaultValue: module.defaultLabel });
    }
    return customWorkspaces.find(({ id }) => id === activeWorkspace)?.name
      || t("commandCenter.modules.items.all", { defaultValue: "All modules" });
  }, [activeWorkspace, customWorkspaces, modules, t]);

  const orderedModules = React.useMemo(() => {
    if (activeWorkspace === "all") return modules;
    return [
      ...modules.filter(({ key }) => key === activeWorkspace),
      ...modules.filter(({ key }) => key !== activeWorkspace),
    ];
  }, [activeWorkspace, modules]);

  const workspacePageGroups = React.useMemo(
    () => groupSidebarPagesForWorkspace(pages)
      .map((group) => {
        const groupMatches = group.title && sidebarPageMatchesQuery(
          { title: group.title },
          workspacePageQuery,
        );
        return {
          ...group,
          visiblePages: groupMatches
            ? group.pages
            : group.pages.filter((page) => sidebarPageMatchesQuery(page, workspacePageQuery)),
        };
      })
      .filter((group) => group.visiblePages.length > 0),
    [pages, workspacePageQuery],
  );
  const selectedWorkspaceUrls = React.useMemo(
    () => new Set(workspaceDraft?.pageUrls || []),
    [workspaceDraft],
  );

  const selectPage = React.useCallback((url) => {
    void prefetchNamespacesForPath(url);
    navigate(url);
    handleOpenChange(false);
  }, [handleOpenChange, navigate]);

  const selectWorkspace = React.useCallback((workspaceKey) => {
    const changed = onSelectWorkspace(workspaceKey);
    if (changed !== false) handleOpenChange(false);
  }, [handleOpenChange, onSelectWorkspace]);

  const openWorkspaceEditor = React.useCallback((workspace) => {
    void ensureSidebarLabelNamespaces();
    setWorkspaceDraft({
      id: workspace?.id || null,
      name: workspace?.name || "",
      pageUrls: [...(workspace?.pageUrls || [])],
    });
    setWorkspacePageQuery("");
    showView("workspace-editor");
  }, [showView]);

  const toggleWorkspacePages = React.useCallback((pageUrls) => {
    setWorkspaceDraft((current) => current ? {
      ...current,
      pageUrls: toggleSidebarPageSelection(current.pageUrls, pageUrls),
    } : current);
  }, []);

  const saveWorkspace = React.useCallback(() => {
    const name = workspaceDraft?.name.trim();
    if (!name || !workspaceDraft.pageUrls.length) return;

    let workspaceId = workspaceDraft.id;
    if (workspaceId) {
      updateCustomWorkspace(preferenceScopeKey, workspaceId, name, workspaceDraft.pageUrls);
    } else {
      workspaceId = createCustomWorkspace(preferenceScopeKey, name, workspaceDraft.pageUrls);
    }
    if (workspaceId) selectWorkspace(workspaceId);
  }, [
    createCustomWorkspace,
    preferenceScopeKey,
    selectWorkspace,
    updateCustomWorkspace,
    workspaceDraft,
  ]);

  const removeWorkspace = React.useCallback(() => {
    if (!workspaceDraft?.id) return;
    const confirmed = window.confirm(t("commandCenter.workspace.editor.deleteConfirm", {
      name: workspaceDraft.name,
      defaultValue: `Delete ${workspaceDraft.name}?`,
    }));
    if (!confirmed) return;
    deleteCustomWorkspace(preferenceScopeKey, workspaceDraft.id);
    setWorkspaceDraft(null);
    showView("workspaces");
  }, [deleteCustomWorkspace, preferenceScopeKey, showView, t, workspaceDraft]);

  const renderPageGroups = () => [
    {
      id: "favorites",
      heading: t("commandCenter.favorites", { defaultValue: "Favorites" }),
      pages: favoritePages,
    },
    {
      id: "destinations",
      heading: t("commandCenter.destinations", { defaultValue: "Destinations" }),
      pages: otherPages,
    },
  ].filter((group) => group.pages.length > 0).map((group) => (
    <CommandGroup key={group.id} heading={group.heading}>
      {group.pages.map((page) => {
        const isFavorite = favoriteUrls.has(page.url);
        return (
          <CommandItem
            key={page.url}
            value={`${page.title} ${page.parent || ""} ${page.grandParent || ""} ${page.url}`}
            onSelect={() => selectPage(page.url)}
            className="min-h-11"
          >
            {page.icon
              ? React.createElement(page.icon, { "aria-hidden": true })
              : <CommandIcon aria-hidden="true" />}
            <span className="min-w-0 flex-1 truncate">{page.title}</span>
            {page.parent && (
              <span className="hidden max-w-40 truncate text-xs text-muted-foreground sm:block">
                {page.parent}
              </span>
            )}
            <button
              type="button"
              aria-pressed={isFavorite}
              aria-label={t(
                isFavorite ? "commandCenter.favorites.remove" : "commandCenter.favorites.add",
                {
                  title: page.title,
                  defaultValue: isFavorite
                    ? `Remove ${page.title} from favorites`
                    : `Add ${page.title} to favorites`,
                },
              )}
              className="-my-2 -mr-2 flex size-11 items-center justify-center rounded-[var(--ds-radius-control)] text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleFavoritePage(preferenceScopeKey, page.url);
              }}
            >
              <Star
                aria-hidden="true"
                className={isFavorite ? "fill-primary text-primary" : undefined}
              />
            </button>
          </CommandItem>
        );
      })}
    </CommandGroup>
  ));

  const renderMainView = () => (
    <>
      <CommandInput
        key="main-search"
        autoFocus
        value={commandQuery}
        onValueChange={setCommandQuery}
        placeholder={t("commandCenter.placeholder", { defaultValue: "Search pages…" })}
      />
      <CommandList className="max-h-[min(60vh,420px)] overscroll-contain">
        <CommandEmpty>{t("commandCenter.empty", { defaultValue: "No matching page found." })}</CommandEmpty>
        <CommandGroup>
          <CommandItem
            value={`${t("commandCenter.workspace.open", { defaultValue: "Choose workspace" })} ${activeWorkspaceLabel}`}
            onSelect={() => showView("workspaces")}
            className="min-h-12"
          >
            <LayoutGrid aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">
                {t("commandCenter.workspace.open", { defaultValue: "Choose workspace" })}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {activeWorkspaceLabel}
              </span>
            </span>
            <ChevronRight aria-hidden="true" />
          </CommandItem>
        </CommandGroup>
        {renderPageGroups()}
      </CommandList>
    </>
  );

  const renderWorkspaceView = () => (
    <>
      <CommandInput
        key="workspace-search"
        autoFocus
        value={commandQuery}
        onValueChange={setCommandQuery}
        placeholder={t("commandCenter.workspace.search", { defaultValue: "Search workspaces…" })}
      />
      <CommandList className="max-h-[min(60vh,420px)] overscroll-contain">
        <CommandEmpty>{t("commandCenter.workspace.empty", { defaultValue: "No matching workspace found." })}</CommandEmpty>
        <CommandGroup>
          <CommandItem value="back command center" onSelect={() => showView("main")} className="min-h-11">
            <ArrowLeft aria-hidden="true" />
            <span>{t("commandCenter.workspace.back", { defaultValue: "Back to Command Center" })}</span>
          </CommandItem>
        </CommandGroup>
        <CommandGroup heading={t("commandCenter.workspace.builtIn", { defaultValue: "Module workspaces" })}>
          <CommandItem
            value={`${t("commandCenter.modules.items.all", { defaultValue: "All modules" })} all modules`}
            onSelect={() => selectWorkspace("all")}
            className="min-h-11"
          >
            <LayoutGrid aria-hidden="true" />
            <span>{t("commandCenter.modules.items.all", { defaultValue: "All modules" })}</span>
            {activeWorkspace === "all" && (
              <CommandShortcut className="tracking-normal">
                {t("commandCenter.modules.active", { defaultValue: "Active" })}
              </CommandShortcut>
            )}
          </CommandItem>
          {orderedModules.map((module) => {
            const Icon = module.icon || CommandIcon;
            const label = t(`commandCenter.modules.items.${module.key}`, {
              defaultValue: module.defaultLabel,
            });
            return (
              <CommandItem
                key={module.key}
                value={`${label} ${module.key} module workspace`}
                onSelect={() => selectWorkspace(module.key)}
                className="min-h-11"
              >
                <Icon aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {activeWorkspace === module.key && (
                  <CommandShortcut className="tracking-normal">
                    {t("commandCenter.modules.active", { defaultValue: "Active" })}
                  </CommandShortcut>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandGroup heading={t("commandCenter.workspace.custom", { defaultValue: "Custom workspaces" })}>
          {customWorkspaces.map((workspace) => (
            <CommandItem
              key={workspace.id}
              value={`${workspace.name} custom workspace`}
              onSelect={() => selectWorkspace(workspace.id)}
              className="min-h-11"
            >
              <LayoutGrid aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{workspace.name}</span>
              {activeWorkspace === workspace.id && (
                <CommandShortcut className="tracking-normal">
                  {t("commandCenter.modules.active", { defaultValue: "Active" })}
                </CommandShortcut>
              )}
              <button
                type="button"
                aria-label={t("commandCenter.workspace.edit", {
                  name: workspace.name,
                  defaultValue: `Edit ${workspace.name}`,
                })}
                className="-my-2 -mr-2 flex size-11 items-center justify-center rounded-[var(--ds-radius-control)] text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openWorkspaceEditor(workspace);
                }}
              >
                <Pencil aria-hidden="true" />
              </button>
            </CommandItem>
          ))}
          <CommandItem
            value="create custom workspace"
            onSelect={() => openWorkspaceEditor(null)}
            className="min-h-11"
          >
            <Plus aria-hidden="true" />
            <span>{t("commandCenter.workspace.create", { defaultValue: "Create custom workspace" })}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </>
  );

  const renderWorkspaceEditor = () => (
    <div className="flex max-h-[min(75vh,620px)] flex-col bg-card">
      <div className="flex items-start gap-2 border-b border-border-subtle p-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => showView("workspaces")}
          aria-label={t("commandCenter.workspace.editor.back", { defaultValue: "Back to workspaces" })}
        >
          <ArrowLeft aria-hidden="true" />
        </Button>
        <div className="min-w-0 pt-1">
          <div className="font-medium">
            {workspaceDraft?.id
              ? t("commandCenter.workspace.editor.editTitle", { defaultValue: "Edit custom workspace" })
              : t("commandCenter.workspace.editor.createTitle", { defaultValue: "Create custom workspace" })}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("commandCenter.workspace.editor.description", {
              defaultValue: "Choose the pages that should appear in this sidebar workspace.",
            })}
          </p>
        </div>
      </div>
      <div className="space-y-[var(--ds-space-4)] overflow-y-auto p-[var(--ds-space-4)]">
        <label className="block space-y-1.5 text-sm font-medium">
          <span>{t("commandCenter.workspace.editor.name", { defaultValue: "Workspace name" })}</span>
          <Input
            autoFocus
            value={workspaceDraft?.name || ""}
            maxLength={48}
            placeholder={t("commandCenter.workspace.editor.namePlaceholder", {
              defaultValue: "For example: Weekly review",
            })}
            onChange={(event) => setWorkspaceDraft((current) => (
              current ? { ...current, name: event.target.value } : current
            ))}
          />
        </label>
        <label className="block space-y-1.5 text-sm font-medium">
          <span>{t("commandCenter.workspace.editor.pages", { defaultValue: "Pages" })}</span>
          <Input
            value={workspacePageQuery}
            placeholder={t("commandCenter.workspace.editor.pageSearch", {
              defaultValue: "Search permitted pages…",
            })}
            onChange={(event) => setWorkspacePageQuery(event.target.value)}
          />
        </label>
        <div className="overflow-hidden rounded-[var(--ds-radius-surface)] border border-border">
          <div className="max-h-[min(38vh,300px)] overflow-y-auto p-1">
            {workspacePageGroups.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("commandCenter.workspace.editor.empty", { defaultValue: "No matching page found." })}
              </p>
            ) : workspacePageGroups.map((group) => {
              const groupUrls = group.pages.map((page) => page.url);
              const selectedCount = groupUrls.filter((url) => selectedWorkspaceUrls.has(url)).length;
              const allSelected = selectedCount === groupUrls.length;
              const partlySelected = selectedCount > 0 && !allSelected;

              return (
                <div key={group.id} className="space-y-0.5">
                  {group.title && (
                    <button
                      type="button"
                      aria-pressed={partlySelected ? "mixed" : allSelected}
                      aria-label={t(
                        allSelected
                          ? "commandCenter.workspace.editor.group.clearAll"
                          : "commandCenter.workspace.editor.group.selectAll",
                        {
                          title: group.title,
                          defaultValue: allSelected
                            ? `Clear all pages under ${group.title}`
                            : `Select all pages under ${group.title}`,
                        },
                      )}
                      className="flex min-h-[var(--ds-density-control-height-lg)] w-full items-center gap-2 rounded-[var(--ds-radius-control)] bg-muted/60 px-2 text-left text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => toggleWorkspacePages(groupUrls)}
                    >
                      <span className={`flex size-5 shrink-0 items-center justify-center rounded border ${allSelected ? "border-primary bg-primary text-primary-foreground" : partlySelected ? "border-primary text-primary" : "border-input bg-background"}`}>
                        {allSelected && <Check aria-hidden="true" className="size-3.5" />}
                        {partlySelected && <Minus aria-hidden="true" className="size-3.5" />}
                      </span>
                      {group.icon
                        ? React.createElement(group.icon, { "aria-hidden": true, className: "size-4 shrink-0" })
                        : <CommandIcon aria-hidden="true" className="size-4 shrink-0" />}
                      <span className="min-w-0 flex-1 truncate">{group.title}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {selectedCount}/{groupUrls.length}
                      </span>
                    </button>
                  )}
                  <div className={group.title ? "space-y-0.5 pl-3" : "space-y-0.5"}>
                    {group.visiblePages.map((page) => {
                      const selected = selectedWorkspaceUrls.has(page.url);
                      const pageContext = page.parent === group.title
                        ? page.grandParent
                        : page.parent;
                      return (
                        <button
                          key={page.url}
                          type="button"
                          aria-pressed={selected}
                          className={`flex min-h-[var(--ds-density-control-height-lg)] w-full items-center gap-2 rounded-[var(--ds-radius-control)] px-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "bg-accent" : ""}`}
                          onClick={() => toggleWorkspacePages([page.url])}
                        >
                          <span className={`flex size-5 shrink-0 items-center justify-center rounded border ${selected ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}>
                            {selected && <Check aria-hidden="true" className="size-3.5" />}
                          </span>
                          {page.icon
                            ? React.createElement(page.icon, { "aria-hidden": true, className: "size-4 shrink-0" })
                            : <CommandIcon aria-hidden="true" className="size-4 shrink-0" />}
                          <span className="min-w-0 flex-1 truncate">{page.title}</span>
                          {pageContext && (
                            <span className="hidden max-w-36 truncate text-xs text-muted-foreground sm:block">
                              {pageContext}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t p-3">
        <span className="mr-auto text-xs text-muted-foreground">
          {t("commandCenter.workspace.editor.selectedCount", {
            count: workspaceDraft?.pageUrls.length || 0,
            defaultValue: "{{count}} pages selected",
          })}
        </span>
        {workspaceDraft?.id && (
          <Button type="button" variant="ghost" className="text-destructive" onClick={removeWorkspace}>
            <Trash2 aria-hidden="true" />
            {t("commandCenter.workspace.editor.delete", { defaultValue: "Delete" })}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => showView("workspaces")}>
          {t("commandCenter.workspace.editor.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          type="button"
          disabled={!workspaceDraft?.name.trim() || !workspaceDraft?.pageUrls.length}
          onClick={saveWorkspace}
        >
          {t("commandCenter.workspace.editor.save", { defaultValue: "Save and use" })}
        </Button>
      </div>
    </div>
  );

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <DialogTitle className="sr-only">
        {t("commandCenter.title", { defaultValue: "Global Command Center" })}
      </DialogTitle>
      <DialogDescription className="sr-only">
        {t("commandCenter.description", {
          defaultValue: "Search permitted pages, open favorites, or change the sidebar workspace.",
        })}
      </DialogDescription>
      {view === "main" && renderMainView()}
      {view === "workspaces" && renderWorkspaceView()}
      {view === "workspace-editor" && renderWorkspaceEditor()}
      {view !== "workspace-editor" && (
        <div className="hidden border-t px-3 py-2 text-xs text-muted-foreground sm:flex">
          {t("commandCenter.hint", {
            defaultValue: "Use arrow keys to navigate and Enter to open",
          })}
          <CommandShortcut>Esc</CommandShortcut>
        </div>
      )}
    </CommandDialog>
  );
}
